import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { PATIENT_LIST_PATH } from '~/components/Header/useReturnToPatientList';
import useUnloadPatient from '~/hooks/patient/useUnloadPatient';
import { pendingReopenRequestState } from './pendingReopenAtom';
import type { OpenIntent } from './resolveOpenIntent';

/**
 * Closes the loaded patient and opens the given connector patient, as the
 * given intent or as the whole patient, through the standard open flow:
 * unload, return to the worklist, and let `OpenInViewerDownloader` consume
 * the open request there. There is no parallel load path, and nothing of the
 * closed patient is on screen when the next one opens.
 *
 * Unsaved changes are discarded with the unload; a caller asks first.
 */
export function useCloseAndOpenPatient(): (
    patientId: string,
    intent?: OpenIntent
) => void {
    const setPendingReopen = useSetRecoilState(pendingReopenRequestState);
    const { unloadCurrentPatient } = useUnloadPatient();
    const navigate = useNavigate();

    return useCallback(
        (patientId: string, intent?: OpenIntent): void => {
            unloadCurrentPatient();
            setPendingReopen(
                intent === undefined ? { patientId } : { patientId, intent }
            );
            navigate(PATIENT_LIST_PATH);
        },
        [unloadCurrentPatient, setPendingReopen, navigate]
    );
}
